import { api } from '@/lib/api-client.ts'
import { AxiosResponse } from 'axios'

/**
 * Notification preference categories (mirror of the backend
 * App\Notification\NotificationCategory enum) and channels
 * (App\Notification\NotificationChannel).
 */
export const NOTIFICATION_CATEGORIES = [
  'mentions',
  'assignments',
  'due_dates',
  'comments',
  'status',
  'details',
] as const
export type NotificationCategoryT = (typeof NOTIFICATION_CATEGORIES)[number]

export const NOTIFICATION_CHANNELS = ['inApp', 'email', 'push'] as const
export type NotificationChannelT = (typeof NOTIFICATION_CHANNELS)[number]

export type EmailFrequencyT = 'instant' | 'digest' | 'off'

/** HIGH-tier categories (instant email + push by default). */
const HIGH_TIER: ReadonlySet<NotificationCategoryT> = new Set([
  'mentions',
  'assignments',
  'due_dates',
])

/**
 * Client-side mirror of PreferenceResolver::channelDefault — used to render the
 * effective state of a matrix cell when the user has no explicit override.
 */
export const channelDefault = (
  category: NotificationCategoryT,
  channel: NotificationChannelT,
): boolean => {
  switch (channel) {
    case 'inApp':
      return true
    case 'email':
      return category !== 'details'
    case 'push':
      return HIGH_TIER.has(category)
  }
}

export type ChannelOverrides = Partial<
  Record<NotificationCategoryT, Partial<Record<NotificationChannelT, boolean>>>
>

export type NotificationPreferenceT = {
  '@id': string
  id: number
  globalEnabled: boolean
  channelOverrides: ChannelOverrides
  emailFrequencyHigh: EmailFrequencyT | null
  emailFrequencyActivity: EmailFrequencyT | null
  digestHour: number
  timezone: string
  quietHoursStart: number | null
  quietHoursEnd: number | null
  pushOnlyWhenAway: boolean
}

export type NotificationPreferenceUpdate = Partial<Omit<NotificationPreferenceT, '@id' | 'id'>>

export const myNotificationPreferenceQuery = () => ({
  queryKey: ['notificationPreference', 'mine'] as const,
  queryFn: async (): Promise<NotificationPreferenceT> => {
    const response: AxiosResponse<NotificationPreferenceT> = await api.get(
      '/notification_preferences/mine',
    )
    return response.data
  },
})

/**
 * The IRI to PATCH for a preference row. Must be the canonical NUMERIC item IRI
 * — NOT `preference['@id']`, which the `/notification_preferences/mine` provider
 * serialises as the read-only `mine` alias (exactly like `/users/me`). That
 * alias route is GET-only, so PATCHing it 405s. Mirrors account/actions.ts,
 * which PATCHes `/users/${id}` rather than the `/users/me` `@id`.
 */
export const notificationPreferenceItemIri = (
  preference: Pick<NotificationPreferenceT, 'id'>,
): string => `/notification_preferences/${preference.id}`

export const updateNotificationPreference = (
  preferenceIri: string,
  patch: NotificationPreferenceUpdate,
): Promise<AxiosResponse<NotificationPreferenceT>> => {
  return api.patch(preferenceIri, patch)
}

/** Effective on/off for a cell, honouring an override then the code default. */
export const isChannelEnabled = (
  preference: NotificationPreferenceT,
  category: NotificationCategoryT,
  channel: NotificationChannelT,
): boolean => {
  const override = preference.channelOverrides?.[category]?.[channel]
  return typeof override === 'boolean' ? override : channelDefault(category, channel)
}

/**
 * Whether this person wants push at all — the server-side answer, independent of
 * any browser.
 *
 * Used to decide whether a device may silently re-register after a logout wiped
 * its registration (see decidePushAutoRegister). Deliberately derived from the
 * same override-then-default resolution the settings matrix renders, rather than
 * from a second copy of the rules: a divergence would mean registering a device
 * for someone whose settings screen shows every push cell off.
 *
 * `globalEnabled` is the master switch, so it short-circuits.
 */
export const wantsPushAnywhere = (preference: NotificationPreferenceT): boolean => {
  if (!preference.globalEnabled) return false
  return NOTIFICATION_CATEGORIES.some(category => isChannelEnabled(preference, category, 'push'))
}

/** Immutably set one cell, returning the next channelOverrides map. */
export const withChannelOverride = (
  overrides: ChannelOverrides,
  category: NotificationCategoryT,
  channel: NotificationChannelT,
  value: boolean,
): ChannelOverrides => ({
  ...overrides,
  [category]: { ...(overrides?.[category] ?? {}), [channel]: value },
})
